/**
 * heroTyping.js
 * 
 * This script manages a typing animation effect for the titles on the homepage's hero section. 
 * It observes changes to the active slide in a Swiper carousel and triggers typing animations 
 * when a new slide becomes active, resetting the text when the slide deactivates.
 * 
 * Key Features:
 * - Ensures the typing effect only applies on the homepage.
 * - Uses a MutationObserver to detect active slide changes in the Swiper.
 * - Manages timers to handle smooth text animation and resets.
 * - Adds ARIA attributes to improve accessibility for screen readers.
 * - Throttles the observer callback to improve performance.
 */

export default function heroTyping() {
    // Check if the body has the 'home' class
    if (!document.body.classList.contains('home')) return; // Exit if not on homepage

    const swiperContainer = document.querySelector('.swiper-container');
    if (!swiperContainer) return; // Exit if Swiper container is not found

    // Throttle the MutationObserver callback to prevent performance issues
    let mutationTimeout;
    const observer = new MutationObserver((mutations) => {
        if (mutationTimeout) return; // Prevent rapid calls

        mutationTimeout = setTimeout(() => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    if (!mutation.target.classList.contains('swiper-slide')) return;

                    if (mutation.target.classList.contains('swiper-slide-active')) {
                        const activeSlide = mutation.target;
                        const h1 = activeSlide.querySelector('h1.heroSection__title');
                        if (h1) {
                            startTypingOnce(h1); // Start typing effect only if it hasn't started
                        }
                    } else {
                        const slide = mutation.target;
                        const h1 = slide.querySelector('h1.heroSection__title');
                        if (h1) resetText(h1); // Reset text on inactive slides
                    }
                }
            });
            mutationTimeout = null; // Reset timeout
        }, 100); // Adjust delay if needed
    });

    // Start observing the Swiper container for attribute changes
    observer.observe(swiperContainer, {
        attributes: true,
        subtree: true,
        attributeFilter: ['class'],
    });

    // Trigger typing effect for the initial active slide if present
    const initialActiveSlide = swiperContainer.querySelector('.swiper-slide-active');
    if (initialActiveSlide) {
        const h1 = initialActiveSlide.querySelector('h1.heroSection__title');
        if (h1) {
            startTypingOnce(h1, 500); // Start typing after 0.5 seconds, only once
        }
    }

    // Add event listener for pagination bullets
    const paginationContainer = swiperContainer.querySelector('.swiper-pagination');
    if (paginationContainer) {
        paginationContainer.addEventListener('click', (event) => {
            if (event.target.classList.contains('swiper-pagination-bullet')) {
                setTimeout(() => {
                    const activeSlide = swiperContainer.querySelector('.swiper-slide-active');
                    if (activeSlide) {
                        const h1 = activeSlide.querySelector('h1.heroSection__title');
                        if (h1) {
                            resetText(h1); // Reset the text in case it was already typed
                            startTypingOnce(h1, 500); // Delay typing effect by 500ms
                        }
                    }
                }, 100); // Allow Swiper to update active slide
            }
        });
    }

    // Function to initialize typing with accessibility enhancements
    function startTypingOnce(element, delay = 1000) {
        // Prevent re-initialization if the typing effect has already started
        if (element.hasAttribute('data-typed')) return;

        if (!element.dataset.fullHtml) {
            element.dataset.fullHtml = element.innerHTML;
        }
        element.setAttribute('aria-live', 'polite'); // Announce content changes to screen readers
        element.setAttribute('aria-hidden', 'false'); // Ensure visibility for accessibility
        element.style.visibility = 'visible'; // Make visible when typing starts

        element.innerHTML = ''; // Clear the text
        setTimeout(() => typeText(element), delay); // Start typing after the specified delay
        element.setAttribute('data-typed', 'true'); // Mark as typed to avoid restart
    }

    // Function to reset the text content and clear any active timers
    function resetText(element) {
        if (element.typingTimer) {
            clearTimeout(element.typingTimer); // Clear existing timer
            delete element.typingTimer;
        }
        if (element.dataset.fullHtml) {
            element.innerHTML = element.dataset.fullHtml; // Restore original text
        }
        element.setAttribute('aria-hidden', 'true'); // Hide from screen readers when inactive
        element.style.visibility = 'hidden'; // Hide text when inactive
        element.removeAttribute('data-typed'); // Remove typed marker on reset
    }

    // Previous helper functions remain the same...
    function measureText(text, element) {
        const measureSpan = document.createElement('span');
        measureSpan.style.visibility = 'hidden';
        measureSpan.style.position = 'absolute';
        measureSpan.style.whiteSpace = 'pre';
        const styles = window.getComputedStyle(element);
        measureSpan.style.font = styles.font;
        measureSpan.style.letterSpacing = styles.letterSpacing;
        measureSpan.style.textTransform = styles.textTransform;
        measureSpan.style.padding = styles.padding;
        measureSpan.innerHTML = text;
        document.body.appendChild(measureSpan);
        const width = measureSpan.offsetWidth;
        document.body.removeChild(measureSpan);
        return width;
    }

    function shouldWrapWord(currentLine, word, element, nextWord) {
        const containerWidth = element.offsetWidth;
        const tolerancePixels = 5;
        
        const lineWithWord = currentLine + word;
        const lineWidth = measureText(lineWithWord, element);
        
        if (lineWidth > containerWidth + tolerancePixels) {
            return true;
        }
        
        if (nextWord && !nextWord.isTag) {
            const lineWithBothWords = lineWithWord + nextWord.text;
            const widthWithBothWords = measureText(lineWithBothWords, element);
            
            if (widthWithBothWords <= containerWidth + tolerancePixels) {
                return false;
            }
            
            if (word.length <= 3) {
                return widthWithBothWords > containerWidth + tolerancePixels * 2;
            }
        }
        
        return lineWidth > containerWidth;
    }

    function getWordBoundaries(text) {
        const words = [];
        let currentWord = '';
        let currentWordStart = 0;

        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            if (char === '<') {
                // Skip HTML tags
                let tagEnd = text.indexOf('>', i);
                if (tagEnd === -1) tagEnd = text.length;
                if (currentWord) {
                    words.push({
                        text: currentWord,
                        start: currentWordStart,
                        end: i
                    });
                    currentWord = '';
                }
                words.push({
                    text: text.substring(i, tagEnd + 1),
                    start: i,
                    end: tagEnd + 1,
                    isTag: true
                });
                i = tagEnd;
            } else if (/\s/.test(char)) {
                if (currentWord) {
                    words.push({
                        text: currentWord,
                        start: currentWordStart,
                        end: i
                    });
                    currentWord = '';
                }
                words.push({
                    text: char,
                    start: i,
                    end: i + 1,
                    isSpace: true
                });
            } else {
                if (!currentWord) {
                    currentWordStart = i;
                }
                currentWord += char;
            }
        }

        if (currentWord) {
            words.push({
                text: currentWord,
                start: currentWordStart,
                end: text.length
            });
        }

        return words;
    }  

    function typeText(element) {
        if (element.typingTimer) {
            clearTimeout(element.typingTimer);
            delete element.typingTimer;
        }
    
        const parentElement = element.closest('.heroSection__titleSlideDown');
        const fullText = element.dataset.fullHtml;
        element.innerHTML = ''; // Clear the text
    
        // Add the blinking cursor class to the parent element
        if (parentElement) parentElement.classList.add('initial-cursor');
    
        // Create a wrapper for the typewriter effect
        const wrapper = document.createElement('div');
        wrapper.style.position = 'relative';
        element.appendChild(wrapper);
    
        let currentLine = document.createElement('div');
        wrapper.appendChild(currentLine);
    
        const cursorSpan = document.createElement('span'); // Dynamic cursor
        cursorSpan.classList.add('active-span');
        currentLine.appendChild(cursorSpan);
    
        const words = getWordBoundaries(fullText);
        let wordIndex = 0;
        let charIndex = 0;
        const typingSpeed = 50;
    
        function updateCursorPosition(span) {
            if (span && span.nextSibling !== cursorSpan) {
                span.parentNode.insertBefore(cursorSpan, span.nextSibling);
            }
        }
    
        function typeNextCharacter() {
            if (wordIndex >= words.length) {
                // Typing complete; ensure the cursor stays on the last character
                const lastSpan = currentLine.lastElementChild;
                updateCursorPosition(lastSpan);
                delete element.typingTimer;
                return;
            }
    
            const currentWord = words[wordIndex];
            const nextWord = words[wordIndex + 1];
    
            if (currentWord.isTag || charIndex === 0) {
                if (!currentWord.isTag && !currentWord.isSpace) {
                    if (shouldWrapWord(currentLine.textContent, currentWord.text, element, nextWord)) {
                        currentLine = document.createElement('div');
                        wrapper.appendChild(currentLine);
    
                        // Move the cursor span to the new line
                        currentLine.appendChild(cursorSpan);
                    }
                }
            }
    
            if (currentWord.isTag) {
                currentLine.innerHTML += currentWord.text;
                wordIndex++;
                charIndex = 0;
                typeNextCharacter();
                return;
            }
    
            const char = currentWord.text[charIndex];
            const span = document.createElement('span');
            span.textContent = char;
            currentLine.insertBefore(span, cursorSpan); // Insert before the cursor span
    
            updateCursorPosition(span); // Ensure the cursor follows
    
            charIndex++;
    
            if (charIndex >= currentWord.text.length) {
                wordIndex++;
                charIndex = 0;
            }
    
            element.typingTimer = setTimeout(typeNextCharacter, typingSpeed);
        }
    
        // Remove the initial blinking cursor from the parent element and start typing
        setTimeout(() => {
            if (parentElement) parentElement.classList.remove('initial-cursor');
            typeNextCharacter();
        }, 0); // No delay in starting typing
    }        
}
